import { ReactElement, useState } from 'react'
import { create as createNiceModal, useModal } from '@ebay/nice-modal-react'
import BaseDrawer from '../../../../../ui-components/modals/BaseDrawer'
import { I18n, Translate2 } from 'common'
import { TableColumn } from '../../interfaces'
import { Button } from '../../../../../ui-components/forms/Button'
import styled from 'styled-components'
import { breakpoints } from '../../../../../theme/breakpoints'
import Box from '@mui/material/Box/Box'
import MakesColumn from './MakesColumn'
import { observer } from 'mobx-react-lite'
import { StoreInstances } from '../../../../../store/StoreInstancesContainer'
import { Checkbox } from '../../../../partsCatalog/Selections/components/Checkbox'
import VehicleServiceProvider from '../../../../../services/VehicleServiceProvider'
import CloseIcon from '@mui/icons-material/Close'
import { LinearProgress } from '@mui/material'

interface Props {
  options: Array<TableColumn>
}

const CustomMakesDrawer = createNiceModal(
  observer((props: Props): ReactElement => {
    const { options } = props
    const modal = useModal()
    const makesStore = StoreInstances.vehicleMakesStore
    const [isSaving, setIsSaving] = useState(false)

    const handleUnSelectAll = (): void => {
      if (isSaving) return
      makesStore.unSelectAllMakes()
    }

    const handleOnClose = (): void => {
      if (isSaving) return
      makesStore.clearBuffer()
      modal.remove()
    }

    const save = async (): Promise<void> => {
      if (isSaving) return

      const makes = makesStore.getSelectedMakes()
      const filterEnabled = makes.length > 0
      setIsSaving(true)
      makesStore.setIsLoadingMakes(true)

      try {
        await VehicleServiceProvider.updateCustomMakes(makes)
        await VehicleServiceProvider.updateMakesFilterStatus(filterEnabled)
        makesStore.persistBuffer()
        makesStore.setMakesFilterEnabled(filterEnabled)
        makesStore.setShowMakesToggle(filterEnabled)
        modal.remove()
      } catch {
        setIsSaving(false)
        makesStore.setIsLoadingMakes(false)
      }
    }

    const title = (
      <Title>
        <I18n name="customizeMakeList" />
        <StyledCloseIcon
          onClick={isSaving ? undefined : handleOnClose}
          $disabled={isSaving}
        />
      </Title>
    )

    const header = (
      <Header>
        <Instructions>
          <I18n name="selectTheMakesThatYouWantToSeeWhenAddingAVehicle" />
        </Instructions>
        <Checkbox
          value=""
          label={Translate2('unSelectAll')}
          onChange={handleUnSelectAll}
          selected={makesStore?.getSelectedMakes()?.length > 0}
          isCheckMixed={makesStore?.getSelectedMakes()?.length > 0}
        />
      </Header>
    )

    const body = (
      <Body>
        {isSaving && <LinearProgress />}
        <Box>
          <InnerWrapper $disabled={isSaving}>
            {options.map((column) => (
              <MakesColumn
                key={`column_${column.id}_${column.header}`}
                column={column.options}
              />
            ))}
          </InnerWrapper>
        </Box>
      </Body>
    )

    const footer = (
      <Footer>
        <Button
          variant="secondary"
          text="cancel"
          onClick={handleOnClose}
          disabled={isSaving}
        />
        <Button
          text="save"
          onClick={save}
          loading={isSaving}
          disabled={isSaving}
        />
      </Footer>
    )

    return (
      <BaseDrawer
        open={modal.visible}
        title={title}
        header={header}
        showHeaderDivider
        body={body}
        showFooterDivider
        footer={footer}
        onClose={handleOnClose}
        paddingX="0"
      />
    )
  })
)

const BaseSection = styled.div`
  padding: 1rem 2rem 1rem 2rem;
  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    padding: 1rem 1rem 1rem 1rem;
  }
`

const Header = styled(BaseSection)`
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  padding-top: 0rem;
  padding-bottom: 0rem;
`

const Body = styled(BaseSection)`
  display: inline-block;
  background-color: #fff;
  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    width: 100%;
  }
`

const Footer = styled(BaseSection)`
  display: Flex;
  justify-content: end;
`

const InnerWrapper = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: row;
  color: ${(p) => p.theme.colors.text};
  max-height: 100%;
  overflow: auto;
  pointer-events: ${(p) => (p.$disabled ? 'none' : 'auto')};
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};

  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    flex-wrap: wrap;
    justify-content: space-between;
    max-height: 60vh;
    overflow: auto;
    padding: 0px;
  }
`

const Instructions = styled.div`
  margin-bottom: 1rem;
  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    display: none;
  }
`

const Title = styled(BaseSection)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-top: 0rem;
  padding-bottom: 0rem;
`

const StyledCloseIcon = styled(CloseIcon)<{ $disabled?: boolean }>`
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.4 : 1)};
`

export default CustomMakesDrawer
